import {ConnectedFocusError} from 'focus-formik-error'
import {Field, Form, Formik} from 'formik'
import {useEffect, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {isValidMail, isValidString} from 'src/_metronic/helpers/FieldValidator'
import {StatusEnums} from 'src/feature/bite/domain/statusEnums'
import * as Yup from 'yup'
import {useCreateUser} from '../../context/useMutation'

const StoreUserCreate = ({loading, onCancel}: {loading: boolean; onCancel: () => void}) => {
  const intl = useIntl()
  const {mutateAsync, data, isLoading: isSubmitting} = useCreateUser()
  const [submit, setSubmit] = useState(false)
  const [, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (data !== null) {
      if (data) {
        setTimeout(() => {
          onCancel()
          setSearchParams({refetch: 'true'})
        }, 3000)

        return
      }

      setSubmit(false)
    }
  }, [data])

  const onSave = async (values) => {
    await mutateAsync({values})
  }

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(intl.formatMessage({id: 'GLOBAL.VALIDATION.EMAIL'}))
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(100, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 100})),
    firstName: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    lastName: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    status: Yup.number().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
  })

  return (
    <>
      {data && (
        <div className='alert alert-success d-flex align-items-center p-5 mb-10'>
          <div className='d-flex flex-column'>
            <h5 className='mb-1'>User Created Successfully</h5>
            <span className='fw-bold text-muted'>
              The user has been successfully created. An email has been sent to the user with the
              login details.
            </span>
          </div>
        </div>
      )}
      <Formik
        initialValues={{
          firstName: '',
          lastName: '',
          middleName: '',
          email: '',
          roleId: '1',
          imageID: '',
          status: 1,
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onSave(values)
          setSubmit(true)
        }}
      >
        {({errors, touched, setFieldValue, values}) => (
          <Form className='form w-100'>
            <ConnectedFocusError />
            <div className='card mb-5 mb-xl-10'>
              <div className='card-header'>
                <div className='card-title m-0'>
                  <div>
                    <h3 className='fw-bold mt-5'>
                      {intl.formatMessage({id: 'STORE.DETAIL.USERS.CREATE'})}
                    </h3>
                    <p className='text-muted fw-normal fs-6'>
                      Add a new user to the portal by entering their name, surname, and email
                      address. An invitation will be sent by email, allowing the user to set their
                      password and access the portal.
                    </p>
                  </div>
                </div>
              </div>
              <div className='card-body p-9'>
                <div className='row'>
                  <div className='col-md-6'>
                    <div className='row mb-7 align-items-center'>
                      <label className='col-lg-4 fw-semibold text-muted'>
                        Name
                        <OverlayTrigger
                          placement='top'
                          overlay={
                            <Tooltip>
                              Enter the first name of the user you want to invite to the portal.
                            </Tooltip>
                          }
                        >
                          <i className='bi bi-info-circle text-primary ms-1'></i>
                        </OverlayTrigger>
                      </label>
                      <div className='col-lg-8'>
                        <Field
                          name='firstName'
                          type='text'
                          className='form-control w-50'
                          placeholder='e.g. John'
                          maxLength={51}
                          onChange={(e) => {
                            setFieldValue('firstName', isValidString(e.target.value))
                          }}
                        />
                        {errors.firstName && touched.firstName ? (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>{errors.firstName}</div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className='row mb-7 align-items-center'>
                      <label className='col-lg-4 fw-semibold text-muted'>
                        Surname
                        <OverlayTrigger
                          placement='top'
                          overlay={
                            <Tooltip>
                              Enter the last name of the user you want to invite to the portal.
                            </Tooltip>
                          }
                        >
                          <i className='bi bi-info-circle text-primary ms-1'></i>
                        </OverlayTrigger>
                      </label>
                      <div className='col-lg-8'>
                        <Field
                          name='lastName'
                          type='text'
                          className='form-control w-50'
                          placeholder='e.g. Doe'
                          maxLength={51}
                          onChange={(e) => {
                            setFieldValue('lastName', isValidString(e.target.value))
                          }}
                        />
                        {errors.lastName && touched.lastName ? (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>{errors.lastName}</div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className='col-md-6'>
                    <div className='row mb-7 align-items-center'>
                      <label className='col-lg-4 fw-semibold text-muted'>
                        Email
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>Add the user's email address.</Tooltip>}
                        >
                          <i className='bi bi-info-circle text-primary ms-1'></i>
                        </OverlayTrigger>
                      </label>
                      <div className='col-lg-8'>
                        <Field
                          name='email'
                          type='text'
                          className='form-control w-50'
                          placeholder='e.g. johndoe@example.com'
                          maxLength={101}
                          onChange={(e) => {
                            setFieldValue('email', isValidMail(e.target.value))
                          }}
                        />
                        {errors.email && touched.email ? (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>{errors.email}</div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className='row mb-7 align-items-center'>
                      <label className='col-lg-4 fw-semibold text-muted'>
                        Status
                        <OverlayTrigger
                          placement='top'
                          overlay={
                            <Tooltip>
                              Select the status of the user. Active users can log in to the portal,
                              while inactive users cannot.
                            </Tooltip>
                          }
                        >
                          <i className='bi bi-info-circle text-primary ms-1'></i>
                        </OverlayTrigger>
                      </label>
                      <div className='col-lg-8'>
                        <select
                          className='form-select w-50'
                          name='status'
                          onChange={(e) => {
                            setFieldValue('status', parseInt(e.target.value))
                          }}
                        >
                          <option value={StatusEnums.Active}>
                            {intl.formatMessage({id: 'ACTIVE'})}
                          </option>
                        </select>
                        {errors.status && touched.status ? (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>{errors.status}</div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='card-footer d-flex w-100 justify-content-end'>
                <div>
                  <button
                    className='btn btn-primary'
                    style={{marginRight: '10px'}}
                    onClick={onCancel}
                  >
                    <i className='bi bi-x me-2'></i>Cancel
                  </button>
                  <button
                    className='btn btn-primary'
                    type='submit'
                    disabled={submit || isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        Save
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    ) : (
                      <>
                        <i className='bi bi-save2 me-2'></i>Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default StoreUserCreate
